import React from 'react';
import { Users, BookOpen, TrendingUp, Star, Plus, Brain, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChildren } from '../../hooks/useChildren';
import { useParentAggregateStats } from '../../hooks/useParentAggregateStats';
import { useTheme } from '../../hooks/useTheme';
import { ChildCard } from '../../components/ChildCard';

export function AdultHomePage() {
  const navigate = useNavigate();
  const { children } = useChildren();
  const { stats, loading } = useParentAggregateStats();
  const { isDarkMode } = useTheme();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">Welcome to Your Dashboard</h1>
        <p className="text-blue-100 text-lg">
          Manage your children's learning journey and track their progress
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className={`rounded-xl p-6 shadow-md border transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Total Children
              </p>
              <p className={`text-3xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.totalChildren}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl p-6 shadow-md border transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Activities Completed
              </p>
              <p className={`text-3xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.totalActivities}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl p-6 shadow-md border transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Average Score
              </p>
              <p className={`text-3xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.averageScore}%
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className={`rounded-xl p-6 shadow-md border transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Total Points
              </p>
              <p className={`text-3xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.totalPoints}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={`rounded-xl p-6 shadow-md border transition-colors duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <h2 className={`text-xl font-bold mb-4 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <button 
            onClick={() => navigate('/adult/children')}
            className={`flex items-center space-x-3 p-4 rounded-2xl transition-all duration-300 shadow-md hover:shadow-lg border ${
              isDarkMode
                ? 'bg-blue-900/20 hover:bg-blue-900/30 border-blue-800 hover:border-blue-700'
                : 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300'
            }`}
          >
            <div className="p-2 bg-blue-500 rounded-lg">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Add New Child
              </div>
              <div className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Create a new learning profile
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/adult/activities')}
            className={`flex items-center space-x-3 p-4 rounded-2xl transition-all duration-300 shadow-md hover:shadow-lg border ${
              isDarkMode
                ? 'bg-purple-900/20 hover:bg-purple-900/30 border-purple-800 hover:border-purple-700'
                : 'bg-purple-50 hover:bg-purple-100 border-purple-200 hover:border-purple-300'
            }`}
          >
            <div className="p-2 bg-purple-500 rounded-lg">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Generate AI Activity
              </div>
              <div className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Create personalized content
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/adult/progress')}
            className={`flex items-center space-x-3 p-4 rounded-2xl transition-all duration-300 shadow-md hover:shadow-lg border ${
              isDarkMode
                ? 'bg-green-900/20 hover:bg-green-900/30 border-green-800 hover:border-green-700'
                : 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300'
            }`}
          >
            <div className="p-2 bg-green-500 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                View Progress
              </div>
              <div className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Track learning analytics
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/adult/billing')}
            className={`flex items-center space-x-3 p-4 rounded-2xl transition-all duration-300 shadow-md hover:shadow-lg border ${
              isDarkMode
                ? 'bg-orange-900/20 hover:bg-orange-900/30 border-orange-800 hover:border-orange-700'
                : 'bg-orange-50 hover:bg-orange-100 border-orange-200 hover:border-orange-300'
            }`}
          >
            <div className="p-2 bg-orange-500 rounded-lg">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Manage Billing
              </div>
              <div className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Subscription & payments
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Children Overview */}
      {children.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Your Children
            </h2>
            <span className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {children.length} child{children.length !== 1 ? 'ren' : ''}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child) => (
              <ChildCard
                key={child.id}
                child={child}
                onClick={() => {}}
                isSelected={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {children.length === 0 && (
        <div className="text-center py-12">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isDarkMode ? 'bg-gray-700' : 'bg-blue-100'
          }`}>
            <Users className={`w-8 h-8 ${
              isDarkMode ? 'text-gray-400' : 'text-blue-600'
            }`} />
          </div>
          <h3 className={`text-xl font-semibold mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            No Children Added Yet
          </h3>
          <p className={`mb-6 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Start by adding your first child to begin their learning journey
          </p>
          <button 
            onClick={() => navigate('/adult/children')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            <span>Add Your First Child</span>
          </button>
        </div>
      )}

    </div>
  );
}